import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Binary,
  Bot,
  DoorOpen,
  FolderKey,
  HeartPulse,
  KeyRound,
  Package,
  Plug,
  ScrollText,
  ShieldCheck,
  Users,
  Wrench,
} from 'lucide-react';

export type AdminNavGroup = 'Users' | 'AI' | 'API' | 'System';

export type AdminNavItem = {
  id: string;
  slug: string;
  navLabel: string;
  navGroup: AdminNavGroup;
  Icon: LucideIcon;
  badgeCount?: number;
  description?: string;
};

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  { id: 'users', slug: 'users', navLabel: 'Users', navGroup: 'Users', Icon: Users },
  {
    id: 'signups',
    slug: 'signups',
    navLabel: 'Signup lobby',
    navGroup: 'Users',
    Icon: DoorOpen,
    badgeCount: 3,
  },
  {
    id: 'sso',
    slug: 'sso',
    navLabel: 'Single sign-on',
    navGroup: 'Users',
    Icon: ShieldCheck,
  },
  {
    id: 'projects',
    slug: 'projects',
    navLabel: 'Project access',
    navGroup: 'Users',
    Icon: FolderKey,
  },
  {
    id: 'connectors',
    slug: 'connectors',
    navLabel: 'Connectors',
    navGroup: 'AI',
    Icon: Plug,
  },
  { id: 'ai-models', slug: 'ai-models', navLabel: 'AI models', navGroup: 'AI', Icon: Bot },
  {
    id: 'builtin-tools',
    slug: 'builtin-tools',
    navLabel: 'Tool baseline',
    navGroup: 'AI',
    Icon: Wrench,
  },
  {
    id: 'system-prompt',
    slug: 'system-prompt',
    navLabel: 'System prompts',
    navGroup: 'AI',
    Icon: ScrollText,
  },
  {
    id: 'predefined-skills',
    slug: 'predefined-skills',
    navLabel: 'Skills library',
    navGroup: 'AI',
    Icon: Package,
  },
  {
    id: 'sandbox-libraries',
    slug: 'sandbox-libraries',
    navLabel: 'Sandbox libraries',
    navGroup: 'AI',
    Icon: Package,
  },
  {
    id: 'embedding-provider',
    slug: 'embedding-provider',
    navLabel: 'Embeddings',
    navGroup: 'AI',
    Icon: Binary,
  },
  {
    id: 'api-keys',
    slug: 'api-keys',
    navLabel: 'Provider keys',
    navGroup: 'API',
    Icon: KeyRound,
  },
  {
    id: 'service-health',
    slug: 'service-health',
    navLabel: 'API health',
    navGroup: 'API',
    Icon: Activity,
  },
  {
    id: 'maintenance',
    slug: 'maintenance',
    navLabel: 'Maintenance',
    navGroup: 'System',
    Icon: Wrench,
  },
  {
    id: 'workspace',
    slug: 'workspace',
    navLabel: 'Workspace',
    navGroup: 'System',
    Icon: FolderKey,
    description: 'Workspace-wide upload and feature toggles.',
  },
  {
    id: 'agent-review-inline',
    slug: 'agent-review-inline',
    navLabel: 'Agent review (inline)',
    navGroup: 'System',
    Icon: ScrollText,
    description: 'Show inline agent-review cards in chat threads.',
  },
  {
    id: 'editor-page-flow',
    slug: 'editor-page-flow',
    navLabel: 'Editor page flow',
    navGroup: 'System',
    Icon: ScrollText,
    description: 'Enable the editor page-flow surface for projects.',
  },
  {
    id: 'system-status',
    slug: 'system-status',
    navLabel: 'System status',
    navGroup: 'System',
    Icon: HeartPulse,
  },
  {
    id: 'announcement-banner',
    slug: 'announcement-banner',
    navLabel: 'Announcement banner',
    navGroup: 'System',
    Icon: ScrollText,
    description: 'Site-wide banner message shown to all users.',
  },
];

export const ADMIN_NAV_GROUP_ORDER: AdminNavGroup[] = ['Users', 'AI', 'API', 'System'];

export function adminNavItemForSlug(slug: string): AdminNavItem | undefined {
  return ADMIN_NAV_ITEMS.find((item) => item.slug === slug);
}
